// Section 15: pregnancy and childbirth, and Section 6's pregnancy rows.
// Conception: per partnered couple with a woman aged 16 to 42, the `conception` target (base 12%
// x happiness x food x housing x Fertile x family size x living apart). Pregnancy lasts 3
// seasons (food 1.3x and 0.7x output in the third, age contributor); each season short on food
// risks a 25% miscarriage and marks the child to be born at health 60. Childbirth: maternal death
// 4% (2% with a healer free, 1% with a healer and 1 cloth, which is used); infant death 8% (4% with
// a healer). The newborn joins the mother's household and inherits one parent trait with 30%
// chance. Constants: config.lifecycle, config.needs, config.people.traitInheritChance.
import { names } from '../../content/names';
import { mintId } from '../../core/ids';
import { clamp, mean } from '../../core/math';
import { ageYears, hasHealerCapacity, peopleSorted } from '../../core/queries';
import type {
  Factor,
  Person,
  PersonId,
  PhaseContext,
  Pregnancy,
  Sex,
  TraitId,
} from '../../core/types';
import { killPerson } from '../shared/death';
import { addStock } from '../shared/stocks';
import { num } from '../shared/value';

function note(source: string, text: string): Factor {
  return { source, op: 'add', value: 0, delta: 0, note: text };
}

function pickName(ctx: PhaseContext, sex: Sex): string {
  const used = new Set<string>();
  for (const p of peopleSorted(ctx.state)) used.add(p.name);
  for (const e of ctx.report.current.entries) if (e.kind === 'birth') used.add(e.name);
  const list = sex === 'female' ? names.female : names.male;
  const free = list.filter((n) => !used.has(n));
  if (free.length > 0) return ctx.rng.pick(free);
  const base = ctx.rng.pick(list);
  let n = 2;
  while (used.has(`${base} ${n}`)) n++;
  return `${base} ${n}`;
}

function inheritedTraits(ctx: PhaseContext, mother: Person, father: Person | undefined): TraitId[] {
  const pool = [...new Set([...mother.traits, ...(father?.traits ?? [])])].sort();
  if (pool.length === 0) return [];
  if (!ctx.rng.happened(ctx.rng.chance(ctx.cfg.people.traitInheritChance))) return [];
  return [ctx.rng.pick(pool)];
}

function newborn(ctx: PhaseContext, mother: Person, preg: Pregnancy): Person {
  const cfg = ctx.cfg;
  const l = cfg.lifecycle;
  const father = preg.fatherId === null ? undefined : ctx.draft.people[preg.fatherId];
  const sex: Sex = ctx.rng.happened(ctx.rng.chance(l.newbornFemaleChance)) ? 'female' : 'male';
  const id = mintId(ctx.draft, 'person');
  const parents = [mother, ...(father === undefined ? [] : [father])];
  const child: Person = {
    id,
    name: pickName(ctx, sex),
    sex,
    ageSeasons: 0,
    health: preg.shortSeasons > 0 ? cfg.needs.newbornHealthIfShort : l.newbornHealth,
    strength: l.newbornStrength,
    happiness: mother.happiness,
    trust: mean(parents.map((p) => p.trust)),
    skills: {},
    skillPeaks: {},
    skillLastUsed: {},
    traits: inheritedTraits(ctx, mother, father),
    status: {
      sick: null,
      injured: null,
      pregnant: null,
      inSchool: false,
      inCustody: null,
      grieving: null,
      stunted: false,
      literate: false,
      guest: null,
      restitution: null,
      quarantined: false,
    },
    householdId: mother.householdId,
    partnerId: null,
    parentIds: [mother.id, ...(preg.fatherId === null ? [] : [preg.fatherId])],
    childIds: [],
    memory: [],
    job: 'rest',
    masterId: null,
    streaks: {
      hungry: 0,
      thirsty: 0,
      cold: 0,
      exposed: 0,
      fed: 0,
      childhoodHungry: 0,
      emigration: 0,
      idle: 0,
      inJob: 0,
      school: 0,
    },
    origin: 'born',
    bornTurn: ctx.season.turn,
    arrivedTurn: null,
    plannedDeparture: null,
    offences: 0,
  };
  ctx.draft.people[id] = child;
  const household = ctx.draft.households[mother.householdId];
  if (household !== undefined) household.memberIds = [...household.memberIds, id].sort();
  for (const parent of parents) parent.childIds = [...parent.childIds, id];
  return child;
}

function childbirth(ctx: PhaseContext, mother: Person, preg: Pregnancy): void {
  const l = ctx.cfg.lifecycle;
  const healer = hasHealerCapacity(ctx.state);
  const cloth = healer && ctx.draft.stocks.cloth.amount >= l.clothPerBirth;
  if (cloth) addStock(ctx, 'cloth', -l.clothPerBirth, 'consumption');
  mother.status.pregnant = null;
  const child = newborn(ctx, mother, preg);
  ctx.report.add({
    kind: 'birth',
    personId: child.id,
    name: child.name,
    motherId: mother.id,
    fatherId: preg.fatherId,
    health: child.health,
  });
  ctx.report.current.population.births += 1;
  const care = healer
    ? note('care:healer', cloth ? 'a healer attended with clean cloth' : 'a healer attended')
    : note('care:none', 'no healer free to attend');
  const hungry =
    preg.shortSeasons > 0
      ? [note('need:food', `short of food ${preg.shortSeasons} seasons of the pregnancy`)]
      : [];
  const infant = healer ? l.infantDeathHealer : l.infantDeath;
  if (ctx.rng.happened(ctx.rng.chance(infant)))
    killPerson(ctx, child.id, 'infant', { factors: [care, ...hungry] });
  const maternal = healer
    ? cloth
      ? l.maternalDeathHealerCloth
      : l.maternalDeathHealer
    : l.maternalDeath;
  if (ctx.rng.happened(ctx.rng.chance(maternal)))
    killPerson(ctx, mother.id, 'childbirth', { factors: [care, ...hungry] });
}

/** Progress pregnancies: miscarriage when short on food, childbirth when due. */
export function progressPregnancies(ctx: PhaseContext): Set<PersonId> {
  const delivered = new Set<PersonId>();
  const short = ctx.cfg.needs.shortThreshold;
  for (const snap of peopleSorted(ctx.state)) {
    if (snap.status.pregnant === null) continue;
    const p = ctx.draft.people[snap.id];
    const preg = p?.status.pregnant ?? null;
    if (p === undefined || preg === null) continue;
    if ((ctx.report.current.needs[p.id]?.food ?? 1) < short) {
      preg.shortSeasons += 1;
      if (ctx.rng.happened(ctx.rng.chance(ctx.cfg.needs.pregnantShortMiscarriage))) {
        p.status.pregnant = null;
        ctx.report.add({ kind: 'miscarriage', personId: p.id });
        continue;
      }
    }
    preg.seasonsRemaining -= 1;
    if (preg.seasonsRemaining <= 0) {
      delivered.add(p.id);
      childbirth(ctx, p, preg);
    }
  }
  return delivered;
}

/** Section 15 conception rolls for couples with a woman of child-bearing age. */
export function conceive(ctx: PhaseContext, delivered: ReadonlySet<PersonId>): void {
  const l = ctx.cfg.lifecycle;
  for (const snap of peopleSorted(ctx.state)) {
    if (snap.sex !== 'female' || snap.status.pregnant !== null || delivered.has(snap.id)) continue;
    const years = ageYears(ctx.cfg, snap.ageSeasons);
    if (years < l.conceptionMinAgeYears || years > l.conceptionMaxAgeYears) continue;
    // Partnered now (pairs formed earlier in this phase count; the dead do not).
    const mother = ctx.draft.people[snap.id];
    if (mother === undefined || mother.partnerId === null || mother.status.pregnant !== null)
      continue;
    const father = ctx.draft.people[mother.partnerId];
    if (father === undefined) continue;
    const chance = clamp(
      num(ctx, { kind: 'conception', householdId: mother.householdId, motherId: mother.id }),
      0,
      1,
    );
    if (chance <= 0 || !ctx.rng.happened(ctx.rng.chance(chance))) continue;
    mother.status.pregnant = {
      seasonsRemaining: l.pregnancySeasons,
      fatherId: father.id,
      shortSeasons: 0,
    };
    ctx.report.add({ kind: 'pregnancy', personId: mother.id, fatherId: father.id });
  }
}
